/**
 * ApiClient - Universal REST API client
 * Provides a clean interface for making HTTP requests with configurable options
 */
class ApiClient {
  constructor(options = {}) {
    this.baseURL = options.baseURL || import.meta.env.VITE_API_BASE_URL || '';
    console.log('API Base URL:', this.baseURL); // Debug log
    this.token = options.token || null;
    this.fakeDelay = options.fakeDelay || 0;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...options.defaultHeaders
    };
    
    // Initialize token from storage after constructor
    console.log('ApiClient constructor - initializing token...');
    this.initializeToken();
    console.log('ApiClient constructor - token after init:', this.token ? 'present' : 'missing');
  }

  /**
   * Fetch available token packages (pricing tiers)
   * @returns {Promise<{ items: Array<{ id: string; tokens: number; price: number; currency?: string; popular?: boolean; description?: string }>, lastUpdated?: string }>}
   */
  async getTokenPackages() {
    return this.get('/billing/token-packages');
  }

  /**
   * Fetch available subscription plans with prices
   * @param {string} [platform] - Platform: 'web' for Stripe, 'mobile' for RevenueCat (optional)
   * @returns {Promise<{ items: Array<{ id: string; name: string; price: number; currency: string; billingPeriod: string; popular?: boolean }> }>}
   */
  async getSubscriptionPlans(platform) {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    const queryString = params.toString();
    return this.get(`/billing/subscription-plans${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Create Stripe checkout session for token purchase
   * @param {string} packageId - Token package ID
   * @param {string} [platform] - Platform: 'web' for Stripe, 'mobile' for RevenueCat (optional)
   * @param {string} [successUrl] - Success redirect URL (optional)
   * @param {string} [cancelUrl] - Cancel redirect URL (optional)
   * @returns {Promise<{ success: boolean; url: string }>}
   */
  async createCheckoutSession(packageId, platform, successUrl, cancelUrl) {
    const params = new URLSearchParams({ packageId });
    if (platform) params.append('platform', platform);
    if (successUrl) params.append('successUrl', successUrl);
    if (cancelUrl) params.append('cancelUrl', cancelUrl);
    return this.post(`/billing/create-checkout?${params.toString()}`);
  }

  /**
   * Create Stripe checkout session for subscription
   * @param {string} plan - Plan type: 'monthly' or 'yearly'
   * @param {string} [platform] - Platform: 'web' for Stripe, 'mobile' for RevenueCat (optional)
   * @param {string} [successUrl] - Success redirect URL (optional)
   * @param {string} [cancelUrl] - Cancel redirect URL (optional)
   * @returns {Promise<{ success: boolean; url: string }>}
   */
  async createSubscriptionCheckout(plan, platform, successUrl, cancelUrl) {
    const params = new URLSearchParams({ plan });
    if (platform) params.append('platform', platform);
    if (successUrl) params.append('successUrl', successUrl);
    if (cancelUrl) params.append('cancelUrl', cancelUrl);
    return this.post(`/billing/create-subscription-checkout?${params.toString()}`);
  }

  /**
   * Create Stripe customer portal session
   * @param {string} [platform] - Platform: 'web' for Stripe, 'mobile' for RevenueCat (optional)
   * @param {string} [returnUrl] - Return URL after portal session (optional)
   * @returns {Promise<{ success: boolean; url: string }>}
   */
  async createCustomerPortal(platform, returnUrl) {
    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    if (returnUrl) params.append('returnUrl', returnUrl);
    const queryString = params.toString();
    return this.post(`/billing/customer-portal${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Verify Stripe checkout session after payment
   * @param {string} sessionId - Stripe Checkout Session ID
   * @returns {Promise<{ success: boolean; paid: boolean; type?: string; tokens?: number; planType?: string; userTokens?: number; isPremium?: boolean }>}
   */
  async verifyCheckoutSession(sessionId) {
    return this.get(`/billing/verify-checkout-session?session_id=${sessionId}`);
  }

  /**
   * Generate image using backend ImageController
   * @param {{ userPrompt: string; boyfriendId: string; userPhotoBase64?: string }} params
   * @returns {Promise<any>} Generate image response
   */
  async generateImage({ userPrompt, boyfriendId, userPhotoBase64 } = {}) {
    return this.post(`/images/generate`, { userPrompt, boyfriendId, userPhotoBase64 });
  }

  /**
   * Get user-generated images for a boyfriend (current user)
   * @param {string} boyfriendId
   * @returns {Promise<Array<{ id: string; imageUrl: string; createdAt: string }>>}
   */
  async getUserGeneratedImagesByBoyfriend(boyfriendId) {
    return this.get(`/images/user-generated/boyfriend/${boyfriendId}`);
  }

  /**
   * Unlock an image by unlockKey
   * @param {string} unlockKey - Unlock key for the image (never minioKey for security)
   * @returns {Promise<{ success: boolean; message?: string; error?: string }>}
   */
  async unlockImage(unlockKey) {
    return this.post('/images/unlock', { unlockKey });
  }

  /**
   * Get spicy photos for a boyfriend with unlock status
   * @param {string} boyfriendId - Boyfriend UUID
   * @returns {Promise<Array<{ url: string; unlockKey: string; locked: boolean; isLockable: boolean; priceTokens: number | null; description: string | null }>>}
   */
  async getSpicyPhotos(boyfriendId) {
    return this.get(`/boyfriends/${boyfriendId}/spicy-photos`);
  }

  /**
   * Add tokens to user account (DEBUG function)
   * @param {number} tokens - Number of tokens to add
   * @returns {Promise<{ success: boolean; message: string; previousTokenCount: number; tokensAdded: number; newTokenCount: number }>}
   */
  async addTokens(tokens) {
    return this.post('/users/addTokens', tokens);
  }

  /**
   * Remove all tokens from current user (DEBUG function)
   */
  async removeAllTokens() {
    return this.post('/users/removeAllTokens', {});
  }

  /**
   * Get recent token transactions for current user
   * @param {number} limit number of records
   */
  async getMyTokenTransactions(limit = 3) {
    return this.get(`/users/me/token-transactions?limit=${encodeURIComponent(limit)}`);
  }

  /**
   * Update user password
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<{ success: boolean; message: string }>}
   */
  async updatePassword(oldPassword, newPassword) {
    return this.put('/users/me/password', {
      oldPassword,
      newPassword
    });
  }

  /**
   * Update user email
   * @param {string} password - Current password
   * @param {string} newEmail - New email address
   * @returns {Promise<{ success: boolean; message: string }>}
   */
  async updateEmail(password, newEmail) {
    return this.put('/users/me/email', {
      password,
      newEmail
    });
  }

  /**
   * Set user credentials (email and password)
   * @param {string} email - Email address
   * @param {string} password - Password
   * @returns {Promise<{ success: boolean; message: string }>}
   */
  async setCredentials(email, password) {
    return this.put('/users/me/credentials', {
      email,
      password
    });
  }

  /**
   * Get user profile
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile() {
    return this.get('/users/me');
  }

  /**
   * Update user profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise<Object>} Updated user profile
   */
  async updateUserProfile(profileData) {
    return this.put('/users/me', profileData);
  }

  /**
   * Update notification settings
   * @param {boolean} notificationsAllowed - Whether to allow notifications
   * @returns {Promise<{ success: boolean; message: string; notificationsAllowed: boolean }>}
   */
  async updateNotifications(notificationsAllowed) {
    return this.put('/users/me/notifications', {
      notificationsAllowed
    });
  }

  /**
   * Delete current user account
   * @returns {Promise<{ success?: boolean; message?: string }>} API response
   */
  async deleteAccount() {
    return this.delete('/users/me');
  }

  /**
   * Get debug prompt parameters by message exchange ID
   * @param {string} messageExchangeId - Message exchange ID
   * @returns {Promise<Object>} Debug prompt data
   */
  async getDebugPromptParams(messageExchangeId) {
    return this.get(`/debug/prompt-params/by-message-exchange/${messageExchangeId}`);
  }

  /**
   * Get authorization header if token is set
   * @returns {Object} Authorization header object
   */
  getAuthHeader() {
    return this.token ? { 'Authorization': `Bearer ${this.token}` } : {};
  }

  /**
   * Get all headers for request
   * @returns {Object} Combined headers object
   */
  getHeaders() {
    return {
      ...this.defaultHeaders,
      ...this.getAuthHeader()
    };
  }

  /**
   * Simulate network delay if fakeDelay is set
   * @param {number} delay - Delay in milliseconds
   */
  async simulateDelay(delay = this.fakeDelay) {
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  /**
   * Build full URL from endpoint
   * @param {string} endpoint - API endpoint
   * @returns {string} Full URL
   */
  buildURL(endpoint) {
    // Remove leading slash from endpoint
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    // Join baseURL and endpoint with single slash
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  /**
   * Refresh access token using refresh token
   * @returns {Promise<string|null>} New access token or null if refresh failed
   */
  async refreshAccessToken() {
    if (this.isRefreshing) {
      // If already refreshing, wait for the existing refresh promise
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this._performTokenRefresh();

    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * Internal method to perform token refresh
   * @returns {Promise<string|null>} New access token or null if refresh failed
   */
  async _performTokenRefresh() {
    try {
      // Get refresh token from cookie
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        console.warn('No refresh token available');
        return null;
      }

      // Call refresh endpoint
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include' // Include cookies
      });

      if (!response.ok) {
        console.warn('Token refresh failed:', response.status);
        return null;
      }

      const data = await response.json();
      
      if (data.accessToken) {
        // Update access token
        this.setToken(data.accessToken);
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('accessToken', data.accessToken);
        }
        return data.accessToken;
      }

      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  }

  /**
   * Initialize token from storage
   */
  initializeToken() {
    const storedToken = this.getStoredToken();
    if (storedToken) {
      this.token = storedToken;
      console.log('Token initialized from storage');
    } else {
      console.log('No token found in storage');
    }
  }

  /**
   * Get stored access token from sessionStorage
   * @returns {string|null} Access token
   */
  getStoredToken() {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem('accessToken');
  }

  /**
   * Get refresh token from cookie
   * @returns {string|null} Refresh token
   */
  getRefreshToken() {
    if (typeof document === 'undefined') return null;
    
    const nameEQ = "refreshToken=";
    const ca = document.cookie.split(';');
    console.log('All cookies:', document.cookie);
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const token = c.substring(nameEQ.length, c.length);
        console.log('Found refresh token in cookies');
        return token;
      }
    }
    console.log('No refresh token found in cookies');
    return null;
  }

  /**
   * Handle response and throw errors if needed
   * @param {Response} response - Fetch response object
   * @returns {Promise<any>} Parsed response data
   */
  async handleResponse(response) {
    console.log(`handleResponse called with status: ${response.status}`);
    if (!response.ok) {
      console.log(`Response not OK: ${response.status} ${response.statusText}`);
      console.log('Has refresh token:', !!this.getRefreshToken());

      // If 401/403, try token refresh
      if ((response.status === 401 || response.status === 403) && this.getRefreshToken()) {
        console.log('Attempting token refresh due to 401/403 error...');
        const newToken = await this.refreshAccessToken();
        if (newToken) {
          console.log('Token refresh successful, retrying request...');
          throw new Error('RETRY_WITH_NEW_TOKEN');
        }
      }

      // Read body ONCE, then try to parse JSON; fallback to raw text
      let raw = '';
      try {
        raw = await response.text();
      } catch (e) {
        // ignore
      }

      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        if (raw) {
          const errJson = JSON.parse(raw);
          errorMessage = errJson.message || errJson.error || raw || errorMessage;
        }
      } catch {
        if (raw) errorMessage = raw;
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }

    // Success: prefer JSON, but safely parse from text
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return await response.json();
    }
    const raw = await response.text();
    try {
      return JSON.parse(raw);
    } catch {
      return raw; // plain text (e.g., image URL)
    }
  }

  /**
   * Make HTTP request
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async request(method, endpoint, options = {}) {
    const {
      data = null,
      headers = {},
      fakeDelay = this.fakeDelay,
      retryCount = 0,
      maxRetries = 1,
      ...fetchOptions
    } = options;

    const url = this.buildURL(endpoint);
    const requestHeaders = {
      ...this.getHeaders(),
      ...headers
    };

    const requestOptions = {
      method: method.toUpperCase(),
      headers: requestHeaders,
      ...fetchOptions
    };

    // Add body for methods that support it
    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      requestOptions.body = JSON.stringify(data);
    }

    try {
      console.log(`Making ${method} request to ${url}`);
      console.log('Current token:', this.token ? 'present' : 'missing');
      
      // Simulate delay if configured
      await this.simulateDelay(fakeDelay);

      const response = await fetch(url, requestOptions);
      console.log(`Response received: ${response.status} ${response.statusText}`);
      return await this.handleResponse(response);
    } catch (error) {
      console.log(`Request error: ${error.message}`);
      // Handle token refresh retry
      if (error.message === 'RETRY_WITH_NEW_TOKEN' && retryCount < maxRetries) {
        console.log(`Retrying request with new token... (attempt ${retryCount + 1}/${maxRetries + 1})`);
        return this.request(method, endpoint, {
          ...options,
          retryCount: retryCount + 1
        });
      }

      // Re-throw our custom errors, wrap others
      if (error.status) {
        throw error;
      }
      
      const networkError = new Error(`Network error: ${error.message}`);
      networkError.originalError = error;
      throw networkError;
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, options = {}) {
    return this.request('GET', endpoint, options);
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data = null, options = {}) {
    return this.request('POST', endpoint, { ...options, data });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, data = null, options = {}) {
    return this.request('PUT', endpoint, { ...options, data });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {any} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async patch(endpoint, data = null, options = {}) {
    return this.request('PATCH', endpoint, { ...options, data });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async delete(endpoint, options = {}) {
    return this.request('DELETE', endpoint, options);
  }

  /**
   * Set authentication token
   * @param {string} token - JWT token
   */
  setToken(token) {
    this.token = token;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('accessToken', token);
    }
  }

  /**
   * Clear authentication token
   */
  clearToken() {
    this.token = null;
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('accessToken');
    }
  }

  /**
   * Clear all tokens (access and refresh)
   */
  clearAllTokens() {
    this.clearToken();
    if (typeof document !== 'undefined') {
      document.cookie = 'refreshToken=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;samesite=strict';
    }
  }

  /**
   * Set base URL
   * @param {string} baseURL - New base URL
   */
  setBaseURL(baseURL) {
    this.baseURL = baseURL;
  }

  /**
   * Set fake delay for testing
   * @param {number} delay - Delay in milliseconds
   */
  setFakeDelay(delay) {
    this.fakeDelay = delay;
  }
}

// Create default singleton instance
const apiClient = new ApiClient();

// Export both the class and the singleton instance
export { ApiClient, apiClient };

// Default export is the singleton instance
export default apiClient;
